/**
 * <StudentHomeGreeting> — le haut de l'accueil de l'élève : son salut, et ce qu'il a gagné.
 *
 * La tête de l'accueil projeté par le livre (chap. 17), avec ce que l'app a : « Salut Damien », en
 * face l'anneau de son prochain titre (là où le livre pose l'anneau de niveau), et dessous la ligne
 * de ses compteurs (« 2 450 XP · 245 pétales · Ninja en herbe »). Le salut est le seul gros texte
 * de la tête : le prénom se coupe avant que l'anneau ne cède.
 *
 * Sans jeu (ni compteurs ni titre), il reste le salut seul. `streak` est la place gardée de la
 * série, en bout de ligne des compteurs, là où le livre pose sa chip : rien aujourd'hui.
 *
 * Rien ne presse ici : le titre se choisit dans « Mon compte », les quêtes s'ouvrent par leur carte.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Title } from "../../atoms/typography/Title"
import { StudentCounters, type StudentCountersProps } from "../../molecules/StudentCounters"
import { TITLE_RING_SIZE, TitleProgress, type TitleProgressProps } from "../../molecules/TitleProgress"

/** Le mot du salut : celui du livre (« Salut Jules »). */
const TEXTS = {
    greeting: (firstname: string) => `Salut ${firstname}`,
}

export type StudentHomeCounters = Pick<StudentCountersProps, "petals" | "xp" | "title">
export type StudentHomeTitleProgress = Pick<TitleProgressProps, "title" | "nextTitle" | "progress">

export type StudentHomeGreetingProps = {
    firstname: string
    /** Ses pétales, son XP, son titre : la ligne sous le salut. Absent : pas de ligne. */
    counters?: StudentHomeCounters | null
    /** Son titre et le chemin vers le suivant : l'anneau en face du salut. Absent : pas d'anneau. */
    titleProgress?: StudentHomeTitleProgress | null
    /** En bout de ligne des compteurs : la place de la série. Rien aujourd'hui. */
    streak?: React.ReactNode
    testID?: string
}

export function StudentHomeGreeting(props: StudentHomeGreetingProps) {
    const { firstname, counters, titleProgress, streak, testID } = props

    return (
        <Stack gap="1" testID={testID}>
            <Row gap="3" justify="between" align="center" style={styles.head}>
                <View style={styles.name}>
                    <Title size="text-3xl" numberOfLines={1}>
                        {TEXTS.greeting(firstname)}
                    </Title>
                </View>
                {titleProgress != null && <TitleProgress {...titleProgress} layout="ring" />}
            </Row>
            <StudentCounters {...counters} variant="text" trailing={streak} />
        </Stack>
    )
}

export type StudentHomeGreetingSkeletonProps = {
    /** Le prénom est connu avant la page (la session) : le salut s'écrit, il n'attend pas. */
    firstname?: string
    /** L'élève a un jeu : l'anneau et la ligne des compteurs attendent sous leur forme. Défaut `true`. */
    game?: boolean
}

/** La forme de la tête pendant le premier chargement : le salut, l'anneau, la ligne des compteurs. */
export function StudentHomeGreetingSkeleton(props: StudentHomeGreetingSkeletonProps) {
    const { firstname, game = true } = props

    return (
        <Stack gap="1">
            <Row gap="3" justify="between" align="center" style={styles.head}>
                <View style={styles.name}>
                    {firstname != null && firstname !== "" ? (
                        <Title size="text-3xl" numberOfLines={1}>
                            {TEXTS.greeting(firstname)}
                        </Title>
                    ) : (
                        <Skeleton textSize="text-3xl" width={180} />
                    )}
                </View>
                {game && <Skeleton shape="circle" size={TITLE_RING_SIZE} />}
            </Row>
            {game && <Skeleton textSize="text-md" width={240} />}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** La tête garde la hauteur de l'anneau, avec ou sans lui : le salut ne saute pas à son arrivée. */
    head: { minHeight: TITLE_RING_SIZE },
    name: { flexShrink: 1, minWidth: 0 },
}))
